import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Lock, CircleCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PaywallContext = createContext({ open: () => {} });
const PERKS = ["SVG, EPS, AI, PDF, PNG & JPG downloads", "Colour versions & AI redesigns of this logo", "Brand guidelines PDF & social kit", "iPhone sticker pack"];

export function PaywallProvider({ children }) {
  const [job, setJob] = useState(null);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/payments/plan").then(({ data }) => setPlan(data)).catch(() => setPlan({ amount: 10, currency: "AUD" }));
  }, []);

  const open = (j) => {
    if (!j?.job_id) return toast.error("Convert a logo first, then unlock it");
    setJob(j);
  };

  const checkout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin, job_id: job.job_id });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start checkout");
      setBusy(false);
    }
  };

  const price = plan ? `${plan.currency === "AUD" ? "A$" : ""}${plan.amount}${plan.currency === "AUD" ? "" : ` ${plan.currency}`}` : "…";

  return (
    <PaywallContext.Provider value={{ open, price }}>
      {children}
      <Dialog open={!!job} onOpenChange={(o) => !o && setJob(null)}>
        <DialogContent className="rounded-none sm:max-w-md" data-testid="paywall-dialog">
          <DialogHeader>
            <div className="w-10 h-10 bg-[#ED5A24] text-white flex items-center justify-center mb-2"><Lock className="w-5 h-5" /></div>
            <DialogTitle className="text-2xl tracking-tight font-bold">Unlock this logo</DialogTitle>
            <DialogDescription data-testid="paywall-logo-name">
              {job?.name ? `“${job.name}” — ` : ""}one payment, every file for this logo. No subscription.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-baseline gap-2 my-2" data-testid="paywall-price">
            <span className="text-5xl font-black tracking-tighter">{price}</span>
            <span className="text-sm text-[#52525B]">per logo · GST included</span>
          </div>
          <ul className="space-y-2 mb-4">
            {PERKS.map((p) => (
              <li key={p} className="flex items-center gap-2.5 text-sm"><CircleCheck className="w-4 h-4 text-[#ED5A24]" />{p}</li>
            ))}
          </ul>
          <button
            type="button"
            onClick={checkout}
            disabled={busy}
            data-testid="paywall-checkout-btn"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#0A0A0B] hover:bg-[#ED5A24] text-white text-xs uppercase tracking-[0.2em] font-bold h-12 transition-colors duration-200 disabled:opacity-60"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Pay {price} & unlock
          </button>
          <p className="text-[11px] text-[#52525B] text-center">Secure checkout by Stripe</p>
        </DialogContent>
      </Dialog>
    </PaywallContext.Provider>
  );
}

export const usePaywall = () => useContext(PaywallContext);
